import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Contact } from '@cockpit/shared';
import { api, ApiError } from '../lib/api';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { ContactForm } from '../components/contacts/ContactForm';
import { InteractionForm } from '../components/contacts/InteractionForm';
import { InteractionList } from '../components/contacts/InteractionList';
import { TagInput } from '../components/contacts/TagInput';
import {
  LABEL_SOURCE_CONTACT,
  LABEL_STATUT_PIPELINE,
  LABEL_TERRITOIRE_CONTACT,
  LABEL_TYPE_CLIENT,
  formatDate,
  formatEuro,
} from '../lib/format';

type Tab = 'infos' | 'interactions' | 'ventes' | 'devis';

export function ContactDetailPage(): JSX.Element {
  const { id } = useParams();
  const contactId = id !== undefined ? Number.parseInt(id, 10) : NaN;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('infos');
  const [editing, setEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const detailQuery = useQuery({
    queryKey: ['contacts', 'detail', contactId],
    queryFn: () => api.contacts.get(contactId),
    enabled: !Number.isNaN(contactId),
  });

  const update = useMutation({
    mutationFn: (body: Parameters<typeof api.contacts.update>[1]) =>
      api.contacts.update(contactId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setEditing(false);
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Erreur mise à jour');
    },
  });

  const remove = useMutation({
    mutationFn: () => api.contacts.delete(contactId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      navigate('/contacts');
    },
  });

  const addTag = useMutation({
    mutationFn: (label: string) => api.contacts.addTag(contactId, label),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });

  const removeTag = useMutation({
    mutationFn: (label: string) => api.contacts.removeTag(contactId, label),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });

  if (Number.isNaN(contactId)) return <div className="text-sm text-red-400">ID invalide.</div>;
  if (detailQuery.isLoading) return <div className="text-sm text-slate-400">Chargement…</div>;
  if (detailQuery.isError || detailQuery.data === undefined)
    return (
      <div className="text-sm text-red-400">
        Contact introuvable.{' '}
        <Link to="/contacts" className="underline">
          Retour à la liste
        </Link>
      </div>
    );

  const { contact, tags, ca_total_ht_centimes, nb_ventes } = detailQuery.data;

  if (editing) {
    return (
      <div className="max-w-5xl">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-slate-100">Modifier le contact #{contact.id}</h1>
        </header>
        {error !== null && (
          <div className="mb-4 text-xs text-red-300 bg-red-900/20 border border-red-900/50 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        <ContactForm
          initial={contact}
          submitting={update.isPending}
          submitLabel="Enregistrer"
          onSubmit={(body): void => update.mutate(body)}
          onCancel={(): void => setEditing(false)}
        />
      </div>
    );
  }

  return (
    <div className="max-w-6xl">
      <header className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs text-slate-500 mb-1">Contact #{contact.id}</div>
          <h1 className="text-2xl font-bold text-slate-100">
            {contact.societe ?? `${contact.prenom ?? ''} ${contact.nom}`.trim()}
          </h1>
          {contact.societe !== null && (
            <p className="text-sm text-slate-400 mt-1">
              {`${contact.prenom ?? ''} ${contact.nom}`.trim()}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={(): void => setEditing(true)}>
            Modifier
          </Button>
          <Button variant="danger" onClick={(): void => setDeleteOpen(true)}>
            Supprimer
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
        <KpiBox
          label="Statut pipeline"
          value={LABEL_STATUT_PIPELINE[contact.statut_pipeline] ?? contact.statut_pipeline}
        />
        <KpiBox
          label="Territoire"
          value={LABEL_TERRITOIRE_CONTACT[contact.territoire] ?? contact.territoire}
        />
        <KpiBox
          label="CA total HT"
          value={formatEuro(ca_total_ht_centimes)}
          hint={`${String(nb_ventes)} vente${nb_ventes > 1 ? 's' : ''}`}
        />
        <KpiBox
          label="Valeur estimée"
          value={formatEuro(contact.valeur_estimee_centimes)}
          hint="Pour le pipeline"
        />
      </div>

      <div className="mb-4">
        <TagInput
          tags={tags}
          onAdd={(label): void => addTag.mutate(label)}
          onRemove={(label): void => removeTag.mutate(label)}
        />
      </div>

      <div className="flex border-b border-slate-800 mb-4">
        <TabBtn active={tab === 'infos'} onClick={(): void => setTab('infos')}>
          Infos
        </TabBtn>
        <TabBtn active={tab === 'interactions'} onClick={(): void => setTab('interactions')}>
          Interactions
        </TabBtn>
        <TabBtn active={tab === 'ventes'} onClick={(): void => setTab('ventes')}>
          Ventes liées ({nb_ventes})
        </TabBtn>
        <TabBtn active={tab === 'devis'} onClick={(): void => setTab('devis')}>
          Devis
        </TabBtn>
      </div>

      {tab === 'infos' && <InfosTab contactId={contactId} contact={contact} />}
      {tab === 'interactions' && <InteractionsTab contactId={contactId} />}
      {tab === 'ventes' && <VentesTab contactId={contactId} />}
      {tab === 'devis' && (
        <div className="text-sm text-slate-500 italic py-12 text-center">
          Onglet Devis — disponible en Phase 4.
        </div>
      )}

      <Modal
        isOpen={deleteOpen}
        onClose={(): void => setDeleteOpen(false)}
        title="Supprimer ce contact ?"
        footer={
          <>
            <Button variant="secondary" type="button" onClick={(): void => setDeleteOpen(false)}>
              Annuler
            </Button>
            <Button
              variant="danger"
              type="button"
              onClick={(): void => remove.mutate()}
              disabled={remove.isPending}
            >
              {remove.isPending ? 'Suppression…' : 'Supprimer'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-300">
          Le contact sera marqué comme supprimé (soft delete). Les ventes et interactions liées sont
          conservées. Restaurable via API.
        </p>
      </Modal>
    </div>
  );
}

// ── Composants internes ────────────────────────────────────────────────────

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-5 py-2.5 text-sm font-medium border-b-2 ${
        active
          ? 'border-floryon-green text-floryon-green'
          : 'border-transparent text-slate-400 hover:text-slate-200'
      }`}
    >
      {children}
    </button>
  );
}

function KpiBox({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}): JSX.Element {
  return (
    <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4">
      <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="text-lg font-semibold text-slate-100 mt-1">{value}</div>
      {hint !== undefined && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function InfosTab({ contactId: _, contact }: { contactId: number; contact: Contact }): JSX.Element {
  return (
    <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-5">
      <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
        <Field
          label="Type client"
          value={LABEL_TYPE_CLIENT[contact.type_client] ?? contact.type_client}
        />
        <Field label="Source" value={LABEL_SOURCE_CONTACT[contact.source] ?? contact.source} />
        <Field label="Téléphone" value={contact.telephone} />
        <Field label="Email" value={contact.email} />
        <Field label="Adresse" value={contact.adresse} />
        <Field
          label="Code postal / Ville"
          value={`${contact.code_postal ?? ''} ${contact.ville ?? ''}`.trim() || '—'}
        />
        <Field label="Département" value={contact.departement} />
        <Field label="SIRET" value={contact.siret} />
        <Field label="TVA intracom" value={contact.tva_intracom} />
        <Field label="Premier contact" value={formatDate(contact.date_premier_contact)} />
        <Field label="Dernière interaction" value={formatDate(contact.date_derniere_interaction)} />
        <Field label="Prochaine relance" value={formatDate(contact.prochaine_relance)} />
      </dl>
      {contact.notes !== null && contact.notes !== '' && (
        <div className="mt-5 pt-4 border-t border-slate-800">
          <div className="text-xs text-slate-500 mb-1">Notes</div>
          <div className="text-sm text-slate-200 whitespace-pre-wrap">{contact.notes}</div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }): JSX.Element {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-200">{value !== null && value !== '' ? value : '—'}</dd>
    </div>
  );
}

function InteractionsTab({ contactId }: { contactId: number }): JSX.Element {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['contacts', 'interactions', contactId],
    queryFn: () => api.contacts.listInteractions(contactId),
  });

  const create = useMutation({
    mutationFn: api.interactions.create,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts', 'interactions', contactId] });
      void queryClient.invalidateQueries({ queryKey: ['contacts', 'detail', contactId] });
      void queryClient.invalidateQueries({ queryKey: ['contacts', 'list'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.interactions.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts', 'interactions', contactId] });
      void queryClient.invalidateQueries({ queryKey: ['contacts', 'detail', contactId] });
      void queryClient.invalidateQueries({ queryKey: ['contacts', 'list'] });
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <InteractionForm
        submitting={create.isPending}
        onSubmit={(d): void =>
          create.mutate({
            contact_id: contactId,
            ...d,
          })
        }
      />
      {query.isLoading && <div className="text-sm text-slate-400">Chargement…</div>}
      {query.isSuccess && (
        <InteractionList items={query.data.items} onDelete={(id): void => remove.mutate(id)} />
      )}
    </div>
  );
}

function VentesTab({ contactId }: { contactId: number }): JSX.Element {
  const query = useQuery({
    queryKey: ['contacts', 'ventes', contactId],
    queryFn: () => api.ventes.list({ contact_id: contactId, page_size: 100 }),
  });
  if (query.isLoading) return <div className="text-sm text-slate-400">Chargement…</div>;
  if (query.isError || query.data === undefined)
    return <div className="text-sm text-red-400">Erreur de chargement.</div>;
  if (query.data.items.length === 0) {
    return (
      <div className="text-sm text-slate-500 italic py-12 text-center">
        Aucune vente liée à ce contact.
      </div>
    );
  }
  const total = query.data.items.reduce((s, v) => s + v.total_ht_centimes, 0);
  return (
    <div className="bg-slate-900/40 border border-slate-800 rounded-xl overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-800 text-sm text-slate-300">
        {query.data.items.length} vente{query.data.items.length > 1 ? 's' : ''} · Total HT{' '}
        {formatEuro(total)}
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-900 text-xs text-slate-400 uppercase tracking-wider">
          <tr>
            <th className="px-4 py-2 text-left">Date</th>
            <th className="px-4 py-2 text-left">N° facture</th>
            <th className="px-4 py-2 text-right">HT</th>
            <th className="px-4 py-2 text-right">TTC</th>
            <th className="px-4 py-2 text-left">Statut</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {query.data.items.map((v) => (
            <tr key={v.id} className="hover:bg-slate-900/60">
              <td className="px-4 py-2">
                <Link
                  to={`/ventes/${String(v.id)}`}
                  className="text-slate-200 hover:text-floryon-green"
                >
                  {formatDate(v.date_vente)}
                </Link>
              </td>
              <td className="px-4 py-2 text-slate-300 text-xs">{v.numero_facture_henrri ?? '—'}</td>
              <td className="px-4 py-2 text-right text-slate-200">
                {formatEuro(v.total_ht_centimes)}
              </td>
              <td className="px-4 py-2 text-right text-floryon-green">
                {formatEuro(v.total_ttc_centimes)}
              </td>
              <td className="px-4 py-2 text-xs text-slate-400">{v.statut_paiement}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
